import { detectSearchLocation } from '@/composables/useSearchLocation';
import { analyticsConsent, OPT_IN_REGIONS } from '@/helpers/googleAnalytics';

const STORAGE_KEY = 'qbFirstTouchAttribution';
const COOKIE_NAME = 'qb_attribution';
const MAX_VALUE_LENGTH = 2048;
const MARKETING_SITE_HOSTS = ['qweebi.com', 'www.qweebi.com'];

export type FirstTouchAttribution = {
	referrer: string;
	landingUrl: string;
};

const readAttributionCookie = (): FirstTouchAttribution | null => {
	const rawCookie = document.cookie
		.split(';')
		.map((cookie) => cookie.trim())
		.find((cookie) => cookie.startsWith(`${COOKIE_NAME}=`))
		?.substring(COOKIE_NAME.length + 1);
	if (rawCookie === undefined || rawCookie === '') {
		return null;
	}
	try {
		const parsed = JSON.parse(decodeURIComponent(rawCookie)) as { r?: unknown; l?: unknown };
		if (typeof parsed.l !== 'string' || parsed.l === '') {
			return null;
		}
		return {
			referrer: typeof parsed.r === 'string' ? parsed.r : '',
			landingUrl: parsed.l,
		};
	} catch {
		return null;
	}
};

const isFromMarketingSite = (referrer: string): boolean => {
	try {
		return MARKETING_SITE_HOSTS.includes(new URL(referrer).hostname);
	} catch {
		return false;
	}
};

const isAllowed = async (locate: () => Promise<string | null>): Promise<boolean> => {
	const choice = analyticsConsent();
	if (choice !== null) {
		return choice === 'granted';
	}
	const country = await locate();
	return country !== null && !OPT_IN_REGIONS.includes(country);
};

export const captureFirstTouchAttribution = async (
	locate = async (): Promise<string | null> => (await detectSearchLocation()).countryCode,
): Promise<void> => {
	try {
		if (localStorage.getItem(STORAGE_KEY) !== null) {
			return;
		}
		const fromCookie = readAttributionCookie();
		if (fromCookie === null && isFromMarketingSite(document.referrer)) {
			return;
		}
		const { referrer, landingUrl } = fromCookie ?? { referrer: document.referrer, landingUrl: window.location.href };
		if (!(await isAllowed(locate))) {
			return;
		}
		localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({
				referrer: referrer.slice(0, MAX_VALUE_LENGTH),
				landingUrl: landingUrl.slice(0, MAX_VALUE_LENGTH),
			}),
		);
	} catch {
		return;
	}
};

export const getFirstTouchAttribution = (): FirstTouchAttribution | null => {
	try {
		const rawValue = localStorage.getItem(STORAGE_KEY);
		if (rawValue === null) {
			return null;
		}
		const parsed = JSON.parse(rawValue) as { referrer?: unknown; landingUrl?: unknown };
		if (typeof parsed.landingUrl !== 'string') {
			return null;
		}
		return {
			referrer: typeof parsed.referrer === 'string' ? parsed.referrer : '',
			landingUrl: parsed.landingUrl,
		};
	} catch {
		return null;
	}
};
